import { getContentItemForLocale } from 'ContentItem/utils';
import { ContentItemIcon } from 'ContentItemIcon';
import { useExpandableContainer } from 'ExpandableContainer';
import { type KeyboardEvent, type FC, useState, useMemo, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { ContentItemBoxExpandableContainer } from './ContentItemBoxExpandableContainer';
import { ContentItemBoxEndIcon } from './ContentItemBoxEndIcon';
import { ContentItemBoxText } from './ContentItemBoxText';
import {
    type ContentItemBoxProps,
    type OnPressOrExpandedContent,
    type WithExpandedContent,
    type WithOnPress,
} from './ContentItemBox.types';

const hasOnPress = (p: OnPressOrExpandedContent): p is WithOnPress => 'onPress' in p;

const hasExpandedContent = (p: OnPressOrExpandedContent): p is WithExpandedContent => 'expandedContent' in p;

const getHeightClass = (height: ContentItemBoxProps['height'], desktopHeight: ContentItemBoxProps['desktopHeight']) => {
    let heightClass = '';
    if (height === 12) heightClass = 'h-12';
    if (height === 14.5) heightClass = 'h-14.5';
    if (height === 16.5) heightClass = 'h-16.5';
    if (height === 18) heightClass = 'h-18';
    let desktopHeightClass = '';

    if (desktopHeight) {
        if (desktopHeight === 12) desktopHeightClass = 'md:h-12';
        if (desktopHeight === 14.5) desktopHeightClass = 'md:h-14.5';
        if (desktopHeight === 16.5) desktopHeightClass = 'md:h-16.5';
        if (desktopHeight === 18) desktopHeightClass = 'md:h-18';
    }

    return twMerge(heightClass, desktopHeightClass);
};

const getClassName = ({
    endIcon,
    onPress,
    addKeydownClass,
    fullWidth,
}: {
    endIcon: JSX.Element | undefined;
    onPress: boolean | (() => void);
    addKeydownClass: boolean;
    fullWidth: boolean;
}) =>
    twMerge(
        'flex flex-col rounded-8 md:rounded-5 bg-white cursor-pointer overflow-hidden',
        'border border-solid border-slate-grey-200 hover:border-slate-grey-300',
        'group',
        'hover:shadow-small',
        'transition-all duration-200',
        endIcon ? 'justify-between' : 'justify-start',
        fullWidth ? 'w-full' : 'w-fit',
        onPress && 'active:scale-[.99]',
        onPress && addKeydownClass && 'scale-[.99]',
    );

export const ContentItemBox: FC<ContentItemBoxProps> = ({
    src,
    localePack,
    locale = 'en',
    imageSize,
    desktopImageSize,
    titleSize = 'base',
    endIcon,
    height = 14.5,
    desktopHeight,
    fullWidth = false,
    subtitle,
    ...rest
}) => {
    const [addKeydownClass, setAddKeydownClass] = useState(false);
    const { open, ExpandableContainer, toggleOpen } = useExpandableContainer();

    const contentItem = useMemo(
        () => getContentItemForLocale(localePack.contentItems, locale),
        [localePack.contentItems, locale],
    );

    const expandedContent = useMemo(() => hasExpandedContent(rest) && rest.expandedContent, [rest]);
    const onPress = useMemo(() => hasOnPress(rest) && rest.onPress, [rest]);

    const onClick = useCallback(() => {
        if (expandedContent) toggleOpen();
        else if (onPress) onPress();
    }, [expandedContent, onPress, toggleOpen]);

    const handleKeyPress = useCallback(
        (type: 'down' | 'up') => (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            if (type === 'down') {
                setAddKeydownClass(true);
                onClick();
                return;
            }
            setAddKeydownClass(false);
        },
        [onClick],
    );

    if (!contentItem) return null;

    return (
        <div className={getClassName({ endIcon, onPress, addKeydownClass, fullWidth })}>
            <ExpandableContainer
                onClick={onClick}
                className={twMerge('flex items-center gap-2 p-3.5', getHeightClass(height, desktopHeight))}
                onKeyDown={handleKeyPress('down')}
                onKeyUp={handleKeyPress('up')}
            >
                <ContentItemIcon
                    src={src}
                    alt={contentItem.title}
                    size={imageSize}
                    desktopSize={desktopImageSize}
                    cacheForever
                />
                <ContentItemBoxText title={contentItem.title} titleSize={titleSize} subtitle={subtitle} />
                <ContentItemBoxEndIcon expandedContent={expandedContent} endIcon={endIcon} open={open} />
            </ExpandableContainer>
            <ContentItemBoxExpandableContainer expandedContent={expandedContent} open={open} />
        </div>
    );
};
